import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { 
  FaUpload, FaDownload, FaFileUpload, FaCheckCircle, 
  FaTimesCircle, FaExclamationTriangle, FaInfoCircle,
  FaArrowLeft, FaTable
} from 'react-icons/fa';
import './BulkUpload.css';

const BulkUpload = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  // Redirect if not seller
  if (user?.userType !== 'seller') {
    return (
      <Layout>
        <div className="bulk-upload-error">
          <FaExclamationTriangle />
          <h2>Access Denied</h2>
          <p>Only sellers can access bulk upload feature</p>
          <button onClick={() => navigate('/products')} className="btn-primary">
            Go to Products
          </button>
        </div>
      </Layout>
    );
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setUploadResult(null);
      } else {
        toast.error('Please select a valid CSV file');
        e.target.value = null;
      }
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/bulk-upload/template', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'product-upload-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Template download error:', error);
      toast.error(error.response?.data?.message || 'Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('skipDuplicates', skipDuplicates);

    try {
      const response = await api.post('/bulk-upload/products', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadResult(response.data);
      toast.success(response.data.message);
      
      // Clear file input
      setFile(null);
      document.getElementById('file-input').value = null;

    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setUploadResult(null);
    document.getElementById('file-input').value = null;
  };

  return (
    <Layout>
      <div className="bulk-upload-page">
        {/* Header */}
        <div className="bulk-upload-header">
          <button className="btn-back" onClick={() => navigate('/products')}>
            <FaArrowLeft /> Back to Products
          </button>
          <div>
            <h1>Bulk Product Upload</h1>
            <p>Upload multiple products at once using a CSV file</p>
          </div>
        </div>

        {/* Instructions Card */}
        <div className="instructions-card">
          <div className="instructions-header">
            <FaInfoCircle />
            <h2>How to Use Bulk Upload</h2>
          </div>
          <div className="instructions-content">
            <div className="instruction-step">
              <span className="step-number">1</span>
              <div className="step-content">
                <h3>Download Template</h3>
                <p>Download the CSV template with sample data and column headers</p>
              </div>
            </div>
            <div className="instruction-step">
              <span className="step-number">2</span>
              <div className="step-content">
                <h3>Fill in Your Data</h3>
                <p>Open the template in Excel or any spreadsheet application and fill in your product details</p>
              </div>
            </div>
            <div className="instruction-step">
              <span className="step-number">3</span>
              <div className="step-content">
                <h3>Upload CSV File</h3>
                <p>Save as CSV and upload the file. Products will be created automatically</p>
              </div>
            </div>
          </div>
          
          <div className="required-fields">
            <h3>Required Fields:</h3>
            <div className="field-tags">
              <span className="field-tag required">title</span>
              <span className="field-tag required">basePrice</span>
              <span className="field-tag optional">description</span>
              <span className="field-tag optional">category</span>
              <span className="field-tag optional">stock</span>
              <span className="field-tag optional">gst (default: 18%)</span>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        <div className="upload-section">
          <div className="upload-card">
            <div className="upload-header">
              <FaTable />
              <h2>Upload Products</h2>
            </div>

            {/* Download Template Button */}
            <button 
              className="btn-download-template"
              onClick={handleDownloadTemplate}
            >
              <FaDownload /> Download CSV Template
            </button>

            {/* File Upload */}
            <div className="file-upload-area">
              <label htmlFor="file-input" className="file-upload-label">
                <FaFileUpload className="upload-icon" />
                <span className="upload-text">
                  {file ? file.name : 'Click to select CSV file or drag and drop'}
                </span>
                <span className="upload-hint">Maximum file size: 5MB</span>
              </label>
              <input
                id="file-input"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="file-input"
              />
            </div>

            {/* Options */}
            {file && (
              <div className="upload-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                  />
                  <span>Skip duplicate products (same title)</span>
                </label>
              </div>
            )}

            {/* Upload Button */}
            {file && (
              <div className="upload-actions">
                <button
                  className="btn-upload"
                  onClick={handleUpload}
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <div className="spinner"></div> Uploading...
                    </>
                  ) : (
                    <>
                      <FaUpload /> Upload Products
                    </>
                  )}
                </button>
                <button
                  className="btn-reset"
                  onClick={handleReset}
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Upload Results */}
        {uploadResult && (
          <div className="upload-results">
            <div className="results-header">
              <h2>Upload Results</h2>
            </div>

            {/* Summary */}
            <div className="results-summary">
              <div className="summary-card success">
                <FaCheckCircle />
                <div>
                  <h3>{uploadResult.summary.successful}</h3>
                  <p>Successful</p>
                </div>
              </div>
              <div className="summary-card error">
                <FaTimesCircle />
                <div>
                  <h3>{uploadResult.summary.failed}</h3>
                  <p>Failed</p>
                </div>
              </div>
              <div className="summary-card warning">
                <FaExclamationTriangle />
                <div>
                  <h3>{uploadResult.summary.skipped}</h3>
                  <p>Skipped</p>
                </div>
              </div>
              <div className="summary-card info">
                <FaInfoCircle />
                <div>
                  <h3>{uploadResult.summary.totalRows}</h3>
                  <p>Total Rows</p>
                </div>
              </div>
            </div>

            <div className="success-rate">
              <p>Success Rate: <strong>{uploadResult.summary.successRate}</strong></p>
            </div>

            {/* Created Products */}
            {uploadResult.createdProducts.length > 0 && (
              <div className="results-section">
                <h3>✅ Created Products ({uploadResult.createdProducts.length})</h3>
                <div className="results-list">
                  {uploadResult.createdProducts.map((product, index) => (
                    <div key={index} className="result-item success">
                      <span className="row-number">Row {product.row}</span>
                      <span className="product-title">{product.title}</span>
                      <span className="product-id">{product.product}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {uploadResult.errors.length > 0 && (
              <div className="results-section">
                <h3>❌ Failed Products ({uploadResult.errors.length})</h3>
                <div className="results-list">
                  {uploadResult.errors.map((error, index) => (
                    <div key={index} className="result-item error">
                      <span className="row-number">Row {error.row}</span>
                      <span className="error-message">{error.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skipped Products */}
            {uploadResult.skippedProducts.length > 0 && (
              <div className="results-section">
                <h3>⚠️ Skipped Products ({uploadResult.skippedProducts.length})</h3>
                <div className="results-list">
                  {uploadResult.skippedProducts.map((product, index) => (
                    <div key={index} className="result-item warning">
                      <span className="row-number">Row {product.rowNumber}</span>
                      <span className="product-title">{product.title || 'N/A'}</span>
                      <span className="skip-reason">{product.reason || 'Error'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="results-actions">
              <button
                className="btn-view-products"
                onClick={() => navigate('/products')}
              >
                View All Products
              </button>
              <button
                className="btn-upload-more"
                onClick={handleReset}
              >
                Upload More Products
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BulkUpload;
